import React from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from 'recharts';
import { HolisticStudent } from '../../../types';
import { TermCard } from '../../../components/shared';

const PEER_BARS = [
  { color: '#6366F1', gradId: 'pg0', from: '#6366F1', to: '#7C3AED' },
  { color: '#7C3AED', gradId: 'pg1', from: '#7C3AED', to: '#8B5CF6' },
  { color: '#F59E0B', gradId: 'pg2', from: '#F59E0B', to: '#FBBF24' },
];

const PeerTab: React.FC = () => {
  const { student } = useOutletContext<{ student: HolisticStudent }>();

  const collaborationScore = student.peerScore;
  const playScore = Math.min(100, student.peerScore + 5);
  const helpingScore = Math.min(100, student.peerScore - 3);

  const chartData = [
    { metric: 'Collaboration',    score: collaborationScore },
    { metric: 'Play Interaction', score: playScore },
    { metric: 'Helping Behavior', score: helpingScore },
  ];

  const isCooperative = student.peerScore >= 70;

  return (
    <div className="space-y-5">
      {/* Status + mini chips */}
      <div className="flex flex-wrap items-center gap-3">
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${
          isCooperative
            ? 'bg-gradient-to-r from-indigo-50 to-violet-50 text-indigo-700 border-indigo-200'
            : 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-700 border-amber-200'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${isCooperative ? 'bg-indigo-500' : 'bg-amber-500'}`} />
          {isCooperative ? '🤝 Cooperative' : '⚠️ Needs Social Support'}
        </span>

        {chartData.map((d, i) => (
          <div key={d.metric} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-indigo-100 bg-gradient-to-r from-indigo-50/60 to-violet-50/40 shadow-sm">
            <div className="w-2 h-2 rounded-full" style={{ background: PEER_BARS[i].color }} />
            <span className="text-xs text-gray-500">{d.metric}:</span>
            <span className="text-sm font-bold text-gray-800">{d.score}</span>
          </div>
        ))}
      </div>

      {/* Bar chart */}
      <div className="rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50/50 to-indigo-50/30 p-5 shadow-sm">
        <p className="text-sm font-semibold text-gray-700 mb-4">Peer Interaction Scores</p>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData} barSize={44}>
            <defs>
              {PEER_BARS.map(b => (
                <linearGradient key={b.gradId} id={b.gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={b.from} stopOpacity={0.85} />
                  <stop offset="100%" stopColor={b.to} stopOpacity={0.5} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EDE9FE" />
            <XAxis dataKey="metric" tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #EDE9FE', fontSize: 12, background: 'rgba(255,255,255,0.95)' }} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
            <Bar dataKey="score" radius={[6, 6, 0, 0]}>
              {chartData.map((_, i) => <Cell key={i} fill={`url(#pg${i})`} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Detail cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TermCard title="Sharing with Peers" items={[
          { term: 'Term 1', text: student.peer.peer_shares_stn_t1 },
          { term: 'Term 2', text: student.peer.peer_shares_stn_t2 },
        ]} />
        <TermCard title="Play Preferences" items={[
          { term: 'Term 1', text: student.peer.peer_likes_play_t1 },
          { term: 'Term 2', text: student.peer.peer_likes_play_t2 },
        ]} />
        <TermCard title="Helping with Tasks" items={[
          { term: 'Term 1', text: student.peer.peer_helps_task_t1 },
        ]} />

        {/* Overall score card with gradient background */}
        <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50 p-5 shadow-sm flex flex-col items-center justify-center">
          <p className="text-xs text-gray-400 mb-2">Overall Peer Score</p>
          <div className="text-5xl font-black text-indigo-600">{student.peerScore}</div>
          <p className="text-xs text-gray-400 mt-1">out of 100</p>
          <div className="mt-3 w-24 h-1.5 bg-indigo-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full"
              style={{ width: `${student.peerScore}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeerTab;
